import React from 'react';
import Backdrop from 'components/Backdrop';
import Button from 'components/Button';

import './index.css';

type AgreementProps = {
    agreeToTerms: () => void;
}

function Agreement({
    agreeToTerms
}: AgreementProps) {
    return (
        <>
            <Backdrop />
            <div className='agreement'>
                <div className='agreement__wrapper'>
                    <h1 className='agreement__title'>
                        PrepSharp User Agreement
                    </h1>
                    <h2 className='agreement__subtitle'>
                        1. Acceptance of Terms
                    </h2>
                    <p className='agreement__text'>
                        By accessing or using the PrepSharp platform (the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use our Service
                    </p>
                    <h2 className='agreement__subtitle'>
                        2. Description of Service
                    </h2>
                    <p className='agreement__text'>
                        PrepSharp provides an online platform for tutors and students to access practice exams and educational testing resources. The Service offers both free and paid access tiers.
                    </p>
                    <h2 className='agreement__subtitle'>
                        3. User Eligibility
                    </h2>
                    <p className='agreement__text'>
                        3.1 Users must be at least 13 years old to use the Service. Users between 13 and 18 require parental or guardian consent.
                    </p>
                    <p className='agreement__text'>
                        3.2 Users are responsible for maintaining the confidentiality of their account credentials and all activities under their account.
                    </p>
                    <h2 className='agreement__subtitle'>
                        4. User Accounts
                    </h2>
                    <p className='agreement__text'>
                        4.1 To access certain features, you must create an account and provide accurate, current, and complete information.
                    </p>
                    <p className='agreement__text'>
                        4.2 You may not:
                    </p>
                    <ul className='agreement__list'>
                        <li>Use another user's account</li>
                        <li>Create multiple accounts to circumvent restrictions</li>
                        <li>Share your account credentials</li>
                        <li>Impersonate another user</li>
                    </ul>
                    <h2 className='agreement__subtitle'>
                        5. Subscription and Payment terms
                    </h2>
                    <p className='agreement__text'>
                        5.1 PrepSharp offers both free and paid subscription levels.
                    </p>
                    <p className='agreement__text'>
                        5.2 Paid Subscriptions:
                    </p>
                    <ul className='agreement__list'>
                        <li>For automatic renewal subscriptions, cancel anytime through account settings</li>
                        <li>No refunds for partial billing periods or unused subscription purchases or renewals</li>
                        <li>Prices subject to change with 30 days notice</li>
                    </ul>
                    <p className='agreement__text'>
                        5.3 Free Tier Limitations:
                    </p>
                    <ul className='agreement__list'>
                        <li>Access to limited practice exams</li>
                        <li>Restricted features compared to paid tiers</li>
                    </ul>
                    <h2 className='agreement__subtitle'>
                        6. Software Platform Use
                    </h2>
                    <p className='agreement__text'>
                        6.1 License: We grant you a non-exclusive, non-transferable, revocable license to use the Platform for its intended purpose. You may not copy, modify, reverse engineer, or distribute the Platform or any part thereof.
                    </p>
                    <p className='agreement__text'>
                        6.2 Acceptable Use: You agree to use the Platform in accordance with these Terms and all applicable laws. You are prohibited from engaging in any activity that is illegal, harmful, or disruptive to the Platform or other users.
                    </p>
                    <p className='agreement__text'>
                        6.3 User Content: You are responsible for any content you upload, post, or transmit through the Platform. We are not responsible for any user-generated content.
                    </p>
                    <p className='agreement__text'>
                        6.4 Termination: We may terminate your access to the Platform at any time for any reason, with or without notice. 
                    </p>
                    <h2 className='agreement__subtitle'>
                        7. Intellectual Property Rights
                    </h2>
                    <p className='agreement__text'>
                        7.1 All content on PrepSharp, including but not limited to practice exams, study materials, and platform design, is the property of PrepSharp and protected by copyright laws.
                    </p>
                    <p className='agreement__text'>
                        7.2 Users may not:
                    </p>
                    <ul className='agreement__list'>
                        <li>Reproduce, distribute, or create derivative works of our content</li>
                        <li>Use content for commercial purposes</li>
                        <li>Remove copyright or proprietary notices</li>
                    </ul>
                    <h2 className='agreement__subtitle'>
                        8. User Content and Conduct
                    </h2>
                    <p className='agreement__text'>
                        8.1 Users may upload certain content such as practice exam results or educational materials.
                    </p>
                    <p className='agreement__text'>
                        8.2 By uploading content, you grant PrepSharp a non-exclusive, worldwide, royalty-free license to use, reproduce, and distribute such content.
                    </p>
                    <p className='agreement__text'>
                        8.3 Prohibited Content:
                    </p>
                    <ul className='agreement__list'>
                        <li>Offensive or discriminatory material</li>
                        <li>Explicit or inappropriate content</li>
                        <li>Content violating third-party rights</li>
                        <li>Spam or unauthorized commercial content</li>
                    </ul>
                    <h2 className='agreement__subtitle'>
                        9. Privacy and Data Protection
                    </h2>
                    <p className='agreement__text'>
                        9.1 Our Privacy Policy, incorporated herein by reference, governs the collection, use, and protection of user data. Our Privacy Policy, available at https://www.prepsharp.com/privacy-terms, details how we collect, use, and protect your personal information. By using the Platform, you consent to the practices described in our Privacy Policy.
                    </p>
                    <p className='agreement__text'>
                        9.2 Collection and Use of Data: We collect certain information about you, including but not limited to, your name, email address, and usage data, as necessary for providing and improving the Platform. We may also collect data through cookies and similar technologies.
                    </p>
                    <p className='agreement__text'>
                        9.3 Third-Party Access: We may share your data with third-party service providers who assist us in providing the Platform. These providers are obligated to protect your data and are bound by confidentiality agreements. 
                    </p>
                    <p className='agreement__text'>
                        9.4 Data Security: We take reasonable measures to protect your data from unauthorized access, use, or disclosure. However, no method of transmission over the internet, or method of electronic storage, is completely secure, and we cannot guarantee absolute security.
                    </p>
                    <h2 className='agreement__subtitle'>
                        10. Limitation of Liability
                    </h2>
                    <p className='agreement__text'>
                        10.1 PrepSharp is provided "as is" without warranties of any kind, either express or implied, including but not limited to, warranties of merchantability, fitness for a particular purpose, or non-infringement.
                    </p>
                    <p className='agreement__text'>
                        10.2 We are not liable for:
                    </p>
                    <ul className='agreement__list'>
                        <li>Direct or indirect damages</li>
                        <li>Loss of data or profits</li>
                        <li>Interruption of service</li>
                        <li>Third-party content or actions</li>
                    </ul>
                    <p className='agreement__text'>
                        10.3 Maximum Liability: Our total liability to you for any damages arising out of or in connection with these Terms shall not exceed the amount you paid for the Platform, if any.
                    </p>
                    <h2 className='agreement__subtitle'>
                        11. Indemnification
                    </h2>
                    <p className='agreement__text'>
                        Users agree to indemnify and hold PrepSharp harmless from any claims, losses, or damages arising from their use of the Service.
                    </p>
                    <h2 className='agreement__subtitle'>
                        12. Termination
                    </h2>
                    <p className='agreement__text'>
                        12.1 PrepSharp may terminate or suspend accounts for:
                    </p>
                    <ul className='agreement__list'>
                        <li>Violation of Terms</li>
                        <li>Fraudulent activity</li>
                        <li>Failure to comply with policies</li>
                    </ul>
                    <p className='agreement__text'>
                        12.2 Users may terminate their account at any time.
                    </p>
                    <h2 className='agreement__subtitle'>
                        13. Dispute Resolution
                    </h2>
                    <p className='agreement__text'>
                        13.1 Any disputes shall be resolved through binding arbitration in Jefferson County, Kentucky.
                    </p>
                    <p className='agreement__text'>
                        13.2 Arbitration will be conducted under the rules of the American Arbitration Association.
                    </p>
                    <h2 className='agreement__subtitle'>
                        14. Governing Law
                    </h2>
                    <p className='agreement__text'>
                        These Terms are governed by the laws of the Commonwealth of Kentucky, without regard to its conflict of law principles.
                    </p>
                    <h2 className='agreement__subtitle'>
                        15. Changes to Terms
                    </h2>
                    <p className='agreement__text'>
                        PrepSharp reserves the right to modify these Terms at any time. Continued use of the Service constitutes acceptance of updated Terms.
                    </p>
                    <h2 className='agreement__subtitle'>
                        16. Entire Agreement
                    </h2>
                    <p className='agreement__text'>
                        These Terms constitute the entire agreement between you and PrepSharp relating to your use of the Platform and supersede all prior or contemporaneous communications and proposals, whether oral or written.
                    </p>
                    <h2 className='agreement__subtitle'>
                        17. Contact Information
                    </h2>
                    <p className='agreement__text'>
                        For questions about these Terms, contact: PrepSharp Legal Department PrepSharp, LLC info@prepsharp.com
                    </p>
                    <p className='agreement__text'>
                        <br/>
                        <b>Last Updated:</b> March 26, 2025
                    </p>
                    <Button
                        className='agreement__button'
                        onClick={agreeToTerms}
                    >
                        I AGREE
                    </Button>
                    <p className='agreement__helper'>
                        <b>BY CLICKING "I AGREE" OR ACCESSING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE.</b>
                    </p>
                </div>
            </div>
        </>
    );
}

export default Agreement;